import { createPortal } from 'react-dom';
import { convertIsoUTCToLocal } from '../../libs.jsx';
import useInfoPopover from './useInfoPopover.js';
import './notes-cell.css';

/**
 * CallsCell — inline indicator for calls placed during an event (Grandstream
 * campuses). Read-only counterpart to NotesCell:
 *   - calls.length === 0 → muted "—"
 *   - calls.length  >  0 → count badge; hover, focus, or a click or tap (pins it)
 *                          shows every call's when / duration / sender → receiver.
 *
 * Call records mirror the Call Logs report shape:
 *   { call_start_time (UTC), call_length, caller, receiver, call_type_name, event_id }
 *
 * @param {{
 *   calls?:      Array<object>,
 *   tzOffset?:   number,   // for localizing call_start_time
 *   ariaPrefix?: string,   // optional context for aria-label
 * }} props
 */
const PREVIEW_W = 320; // .nc-popover's width

const CallsCell = ({ calls, tzOffset, ariaPrefix }) => {
  const list = Array.isArray(calls) ? calls : [];
  const count = list.length;
  const { open, style, panelId, triggerProps } = useInfoPopover({
    maxWidth: PREVIEW_W, flipBelow: 220, enabled: count > 0,
  });

  if (count === 0) {
    return <span className="nc-disabled" aria-hidden="true">{'—'}</span>;
  }

  const label = `${ariaPrefix ? ariaPrefix + ' — ' : ''}${count} call${count !== 1 ? 's' : ''} placed during this event`;

  return (
    <span className="nc-cell" aria-label={label} {...triggerProps}>
      <span className="nc-badge" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.74a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z" />
        </svg>
        {count}
      </span>
      {open && style ? createPortal(
        <span id={panelId} className="nc-popover" style={style} role="tooltip">
          <span className="nc-popover__title">Calls ({count})</span>
          <ul className="nc-popover__list">
            {list.map((c, i) => (
              <li key={c.call_id || i} className="nc-popover__item">
                <span className="nc-popover__meta">
                  <span>{c.call_start_time ? convertIsoUTCToLocal(c.call_start_time, tzOffset) : '—'}</span>
                  <span>{c.call_length || '—'}</span>
                </span>
                <span className="nc-popover__text">
                  {(c.caller || '—')} {'→'} {(c.receiver || '—')}
                </span>
              </li>
            ))}
          </ul>
        </span>,
        document.body,
      ) : null}
    </span>
  );
};

export default CallsCell;
